import React, { useState, useRef, useEffect, type ReactNode } from 'react';
import { Button } from 'pink-lava-ui';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface ExpandableContainerProps {
  children: ReactNode;
  minimizedHeight?: number;
  buttonText?: string;
  buttonPlacement?: 'left' | 'center' | 'right';
  className?: React.HTMLAttributes<HTMLDivElement>['className'];
  disabled?: boolean;
  extra?: any;
  onExpandedChange?: (_expanded: boolean) => void;
}

const ExpandableContainer = ({
  children,
  minimizedHeight = 200,
  buttonText = 'Tampilkan Semua',
  buttonPlacement = 'center',
  className = '',
  disabled = false,
  onExpandedChange,
}: ExpandableContainerProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldShowButton, setShouldShowButton] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [originalHeight, setOriginalHeight] = useState<number>(0);

  useEffect(() => {
    if (contentRef.current) {
      const fullHeight = contentRef.current.scrollHeight;
      if (fullHeight !== originalHeight) setOriginalHeight(fullHeight);

      setShouldShowButton(!disabled && fullHeight > minimizedHeight);
    }
  }, [children, minimizedHeight, disabled, originalHeight]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);

    onExpandedChange?.(!isExpanded);
  };

  const getButtonPlacementClass = () => {
    switch (buttonPlacement) {
      case 'left':
        return 'justify-start';
      case 'right':
        return 'justify-end';
      case 'center':
      default:
        return 'justify-center';
    }
  };

  const currentHeight = isExpanded ? originalHeight : minimizedHeight;

  return (
    <div className={`relative space-y-1 ${className}`}>
      {/* Content container with smooth height transition */}
      <div
        ref={contentRef}
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{
          height: shouldShowButton ? `${currentHeight}px` : 'auto',
        }}
      >
        {children}
      </div>

      {/* Gradient overlay when collapsed */}
      {/* {!isExpanded && shouldShowButton && (
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none" />
      )} */}

      {/* Expand/Collapse button */}
      {shouldShowButton && (
        <div className={`flex w-full ${getButtonPlacementClass()}`}>
          <Button size="small" type="ghost" onClick={toggleExpanded} className="p-1! h-[24px]!">
            {isExpanded ? 'Sembunyikan' : buttonText}
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ExpandableContainer;
